import { useState, useEffect, useCallback, useMemo, useRef } from 'react';

/**
 * Hook for managing in-draft trading
 * Handles trade proposals, responses, and history
 */
export const useDraftTrades = (socket, myTeam, leagueId) => {
  // Trade state
  const [pendingTrades, setPendingTrades] = useState([]);
  const [tradeHistory, setTradeHistory] = useState([]);
  const [incomingTrade, setIncomingTrade] = useState(null);
  const [isProposing, setIsProposing] = useState(false);
  const [tradeError, setTradeError] = useState(null);
  const [tradeSuccess, setTradeSuccess] = useState(null);

  // Hydrate pending trades from initial draft-state (only once)
  const initialTradesLoadedRef = useRef(false);
  useEffect(() => {
    if (!socket) return;

    const handleDraftStateForTrades = (state) => {
      if (!initialTradesLoadedRef.current && state.pendingTrades && Array.isArray(state.pendingTrades) && state.pendingTrades.length > 0) {
        setPendingTrades(state.pendingTrades);
        initialTradesLoadedRef.current = true;
      }
      if (state.completedTrades && Array.isArray(state.completedTrades) && state.completedTrades.length > 0) {
        setTradeHistory(state.completedTrades.map(t => ({
          ...t,
          status: 'completed',
          completed_at: t.resolved_at
        })));
      }
    };

    socket.on('draft-state', handleDraftStateForTrades);
    return () => socket.off('draft-state', handleDraftStateForTrades);
  }, [socket]);

  // Socket event handlers
  useEffect(() => {
    if (!socket) return;

    // Incoming trade proposal
    const handleTradeProposalReceived = (data) => {
      console.log('📬 Trade proposal received:', data);
      setIncomingTrade(data);
      setPendingTrades(prev => [...prev, data.trade]);
    };

    // Trade proposal sent confirmation
    const handleTradeProposalSent = (data) => {
      console.log('📤 Trade proposal sent:', data);
      setPendingTrades(prev => [...prev, data.trade]);
      setIsProposing(false);
      const teamName = data.trade?.receiving_team_name || data.receiving_team_name || 'team';
      setTradeSuccess(`Trade proposal sent to ${teamName}!`);
      setTimeout(() => setTradeSuccess(null), 5000);
    };

    // Trade response received (accept/reject)
    const handleTradeResponseReceived = (data) => {
      console.log('📩 Trade response received:', data);

      // Move from pending to history
      setPendingTrades(prev => prev.filter(t => t.trade_id !== data.trade_id));
      setTradeHistory(prev => [{
        ...data,
        completed_at: new Date().toISOString()
      }, ...prev]);
    };

    // Trade executed (broadcast to all)
    const handleTradeExecuted = (data) => {
      console.log('✅ Trade executed:', data);

      // Move from pending to history with success status
      setPendingTrades(prev => prev.filter(t => t.trade_id !== data.trade_id));
      setTradeHistory(prev => [{
        trade_id: data.trade_id,
        status: 'completed',
        affected_picks: data.affected_picks,
        completed_at: new Date().toISOString()
      }, ...prev]);

      // Clear incoming trade notification if it was this trade
      setIncomingTrade(prev => {
        if (prev?.trade?.trade_id === data.trade_id) {
          return null;
        }
        return prev;
      });
    };

    // Trade cancelled
    const handleTradeCancelled = (data) => {
      console.log('❌ Trade cancelled:', data);
      setPendingTrades(prev => prev.filter(t => t.trade_id !== data.trade_id));

      setIncomingTrade(prev => {
        if (prev?.trade?.trade_id === data.trade_id) {
          return null;
        }
        return prev;
      });
    };

    // Trade error
    const handleTradeError = (data) => {
      console.error('⚠️ Trade error:', data);
      setTradeError(data.message);
      setIsProposing(false);
    };

    // Register handlers
    socket.on('trade-proposal-received', handleTradeProposalReceived);
    socket.on('trade-proposal-sent', handleTradeProposalSent);
    socket.on('trade-response-received', handleTradeResponseReceived);
    socket.on('trade-executed', handleTradeExecuted);
    socket.on('trade-cancelled', handleTradeCancelled);
    socket.on('error', handleTradeError);

    return () => {
      socket.off('trade-proposal-received', handleTradeProposalReceived);
      socket.off('trade-proposal-sent', handleTradeProposalSent);
      socket.off('trade-response-received', handleTradeResponseReceived);
      socket.off('trade-executed', handleTradeExecuted);
      socket.off('trade-cancelled', handleTradeCancelled);
      socket.off('error', handleTradeError);
    };
  }, [socket]);

  // Propose a trade
  const proposeTrade = useCallback((proposal) => {
    if (!socket || !myTeam) {
      setTradeError('Not connected or no team');
      return;
    }

    setIsProposing(true);
    setTradeError(null);

    socket.emit('propose-trade', {
      league_id: leagueId,
      proposing_team_id: myTeam.team_id,
      receiving_team_id: proposal.receiving_team_id,
      offering_picks: proposal.offering_picks,
      requesting_picks: proposal.requesting_picks,
      offering_players: proposal.offering_players,
      requesting_players: proposal.requesting_players,
    });
  }, [socket, myTeam, leagueId]);

  // Respond to a trade (accept/reject)
  const respondToTrade = useCallback((tradeId, response) => {
    if (!socket) {
      setTradeError('Not connected');
      return;
    }

    socket.emit('respond-to-trade', {
      trade_id: tradeId,
      response // 'accept' or 'reject'
    });

    // Clear incoming notification using functional update
    setIncomingTrade(prev => {
      if (prev?.trade?.trade_id === tradeId) {
        return null;
      }
      return prev;
    });
  }, [socket]);

  // Cancel a pending trade (only proposer can cancel)
  const cancelTrade = useCallback((tradeId) => {
    if (!socket) return;

    socket.emit('cancel-trade', {
      trade_id: tradeId
    });
  }, [socket]);

  // Dismiss incoming trade notification
  const dismissIncomingTrade = useCallback(() => {
    setIncomingTrade(null);
  }, []);

  // Clear trade error
  const clearTradeError = useCallback(() => {
    setTradeError(null);
  }, []);

  // Computed: trades I proposed
  const myProposedTrades = useMemo(() => {
    if (!myTeam) return [];
    return pendingTrades.filter(t => t.proposing_team_id === myTeam.team_id);
  }, [pendingTrades, myTeam]);

  // Computed: trades proposed to me
  const tradesProposedToMe = useMemo(() => {
    if (!myTeam) return [];
    return pendingTrades.filter(t => t.receiving_team_id === myTeam.team_id);
  }, [pendingTrades, myTeam]);

  // Computed: has pending trades
  const hasPendingTrades = useMemo(() =>
    pendingTrades.length > 0, [pendingTrades]);

  // Computed: has incoming trade
  const hasIncomingTrade = useMemo(() =>
    incomingTrade !== null, [incomingTrade]);

  return {
    // State
    pendingTrades,
    tradeHistory,
    incomingTrade,
    isProposing,
    tradeError,
    tradeSuccess,

    // Actions
    proposeTrade,
    respondToTrade,
    cancelTrade,
    dismissIncomingTrade,
    clearTradeError,

    // Computed
    myProposedTrades,
    tradesProposedToMe,
    hasPendingTrades,
    hasIncomingTrade
  };
};

export default useDraftTrades;
